/**
 * TerminalGrid — 2-col rows-of-2 layout
 *
 * Renders terminals in a 2-col grid via nested react-resizable-panels:
 * outer vertical PanelGroup (rows) wrapping inner horizontal PanelGroups
 * (cells). N entries chunk into ceil(N/2) rows of up to 2 each:
 *   - 4 entries -> [2, 2]   clean 2x2
 *   - 5 entries -> [2, 2, 1]
 *   - 6 entries -> [2, 2, 2]
 *   - 1 entry   -> [1] (full width, no handles)
 *
 * Resize handles live both between rows (horizontal bar) and between
 * cells inside a row (vertical bar). Empty list renders nothing.
 *
 * Auth: TerminalTile inherits the SPA's ForwardAuth-gated session via the
 * WebSocket cross-subdomain cookie.
 */
import { Fragment } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { TerminalTile } from './TerminalTile';

export interface TerminalEntry {
  url: string;
  label: string;
  onClose?: () => void; // Y.1a — when provided, the tile shows an "x" close button
  onRename?: (newDisplayName: string | null) => void; // Y.3 — click-to-rename header label
}

export interface TerminalGridProps {
  terminals: TerminalEntry[];
}

const ROW_SIZE = 2;

function chunk<T>(arr: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    rows.push(arr.slice(i, i + size));
  }
  return rows;
}

function RowResizeHandle() {
  return (
    <PanelResizeHandle className="group relative flex items-center justify-center">
      <div className="h-1 w-full bg-vibe-border group-hover:bg-vibe-accent/50 group-active:bg-vibe-accent transition-colors" />
    </PanelResizeHandle>
  );
}

function CellResizeHandle() {
  return (
    <PanelResizeHandle className="group relative flex items-center justify-center">
      <div className="w-1 h-full bg-vibe-border group-hover:bg-vibe-accent/50 group-active:bg-vibe-accent transition-colors" />
    </PanelResizeHandle>
  );
}

export function TerminalGrid({ terminals }: TerminalGridProps) {
  if (terminals.length === 0) {
    return null;
  }

  const rows = chunk(terminals, ROW_SIZE);
  const rowSize = 100 / rows.length;

  return (
    <PanelGroup direction="vertical" className="h-full w-full">
      {rows.map((row, rowIdx) => {
        const cellSize = 100 / row.length;
        return (
          <Fragment key={`row-${rowIdx}`}>
            {rowIdx > 0 && <RowResizeHandle />}
            <Panel id={`terminal-row-${rowIdx}`} defaultSize={rowSize} minSize={10}>
              <PanelGroup direction="horizontal" className="h-full w-full">
                {row.map((t, colIdx) => {
                  const flatIdx = rowIdx * ROW_SIZE + colIdx;
                  return (
                    <Fragment key={t.url}>
                      {colIdx > 0 && <CellResizeHandle />}
                      <Panel
                        id={`terminal-cell-${flatIdx}`}
                        defaultSize={cellSize}
                        minSize={10}
                      >
                        <TerminalTile url={t.url} label={t.label} onClose={t.onClose} onRename={t.onRename} />
                      </Panel>
                    </Fragment>
                  );
                })}
              </PanelGroup>
            </Panel>
          </Fragment>
        );
      })}
    </PanelGroup>
  );
}
